/**
 * THE VIDEO ITSELF (v1.0.29): choose where it comes from, then watch it go in.
 *
 *   No video yet  "Upload a file" (drop or choose an MP4 or MOV; progress, cancel, "keep this page
 *                 open") or "Paste a YouTube link" (the link, then "Add video").
 *   Uploaded      "Processing": the media worker is checking and converting it; the editor polls
 *                 (./use-article-video.ts) and the author keeps writing meanwhile.
 *   Not attached  The bytes went in and `setArticleVideo` was refused: why, and "Try again", which
 *                 names the same upload again (./use-video-upload.ts) - or another file.
 *   Failed        Why, in plain words from the worker's code, and the way to try another file.
 *   Ready         The player - `<video>` over the signed address, or YouTube's privacy-enhanced
 *                 embed built from the id alone, so nothing the author typed is ever rendered -
 *                 with "Replace video" and "Remove". A YouTube video's orientation is the author's
 *                 to say; an upload's is measured.
 *
 * Every control is disabled where the author may not write the draft; the player stays.
 *
 * A LIVE STREAM (1.2.0, `stream`) is a YouTube link and nothing else - the platform refuses an
 * upload for one - so it is offered the link alone, under its own heading.
 *
 * ★ FOCUS FOLLOWS THE SWAP. Choosing a file, Cancel, Replace, "Keep the current video" and Remove
 * each take away the control that was pressed and draw another pane in its place. So focus moves to
 * the new pane's first control (`data-video-focus`), or to the block's heading where it has none -
 * when an action here asked for it, or when focus fell to the page because its control went. A
 * swap the author did not cause (the server finished preparing) leaves focus wherever it is.
 */
import {
  Alert,
  Button,
  FileButton,
  Group,
  Loader,
  Progress,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import { CloudUpload, RectangleHorizontal, RectangleVertical } from '@moonx/ui';
import { useEditorApi } from '../../features/cabinet/api/editor-api';
import { describeProblem } from '../../features/cabinet/api/problem';
import type { ArticleVideo, VideoOrientation } from '../../features/cabinet/api/editor-api';
import { ConfirmAction, ErrorState } from '../../features/cabinet/components';
import { useAction } from '../../features/cabinet/state';
import { useTranslation } from '../../i18n';
import type { ArticleVideoState } from './use-article-video';
import { VIDEO_ACCEPT, useVideoUpload } from './use-video-upload';
import type { VideoUpload } from './use-video-upload';
import type { VideoT } from './video-i18n';
import {
  describeVideoFailure,
  describeVideoUploadFailure,
  describeYoutubeFailure,
  formatDuration,
  formatResolution,
  formatSize,
} from './video-words';
import classes from './video.module.css';

type Source = 'upload' | 'youtube';

export interface VideoBlockProps {
  readonly articleId: string;
  readonly state: ArticleVideoState;
  readonly disabled: boolean;
  /** A live stream: its YouTube link only, no file. */
  readonly stream?: boolean;
}

/** The YouTube player for an id, in its privacy-enhanced mode (the cabinet's CSP allows it). */
export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`;
}

/** Marks the control a pane hands focus to when it takes the place of another. */
const FOCUS_TARGET = { 'data-video-focus': true } as const;

export function VideoBlock({ articleId, state, disabled, stream = false }: VideoBlockProps) {
  const { t } = useTranslation('video');
  const api = useEditorApi();
  const { video, adopt } = state;
  const upload = useVideoUpload(articleId);
  const [replacing, setReplacing] = useState(false);
  const [source, setSource] = useState<Source>(stream ? 'youtube' : (video?.source ?? 'upload'));
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Set by an action here that swaps the pane; spent by the swap it causes.
  const focusAsked = useRef(false);
  const askFocus = () => {
    focusAsked.current = true;
  };

  const attachLink = useAction(async (url: string, orientation?: VideoOrientation) =>
    api.setArticleVideo(articleId, {
      source: 'youtube',
      url,
      ...(orientation === undefined ? {} : { orientation }),
    }),
  );
  const remove = useAction(async () => {
    await api.removeArticleVideo(articleId);
    return true as const;
  });

  // An upload in flight when the draft stops being writable cannot be attached: let it go.
  const { cancel } = upload;
  useEffect(() => {
    if (disabled) {
      cancel();
    }
  }, [disabled, cancel]);

  const goingIn = upload.state.phase === 'uploading' || upload.state.phase === 'attaching';
  const choosing = video === null || replacing || goingIn;

  // Which pane is drawn: a change of it is a swap (see the header).
  const pane = choosing
    ? goingIn
      ? upload.state.phase
      : `choose:${source}`
    : `video:${video?.state ?? 'none'}`;
  const shownPane = useRef(pane);
  useLayoutEffect(() => {
    if (shownPane.current === pane) {
      return;
    }
    shownPane.current = pane;
    const fell = document.activeElement === null || document.activeElement === document.body;
    if (!focusAsked.current && !fell) {
      return;
    }
    const asked = focusAsked.current;
    focusAsked.current = false;
    const target =
      sectionRef.current?.querySelector<HTMLElement>('[data-video-focus]') ?? headingRef.current;
    // Asked for, the page may scroll to it; merely fallen, it is put back without a jump.
    target?.focus({ preventScroll: !asked });
  }, [pane]);

  const take = async (attaching: Promise<ArticleVideo | undefined>) => {
    const attached = await attaching;
    if (attached !== undefined) {
      adopt(attached);
      setReplacing(false);
    }
  };

  const onFile = (file: File) => {
    askFocus();
    void take(upload.upload(file));
  };

  const onRetryAttach = () => {
    askFocus();
    void take(upload.retryAttach());
  };

  const onLink = (url: string) => {
    void (async () => {
      const attached = await attachLink.run(url);
      if (attached !== undefined) {
        askFocus();
        adopt(attached);
        setReplacing(false);
      }
    })();
  };

  const onRemove = async () => {
    if ((await remove.run()) === true) {
      askFocus();
      adopt(null);
      setReplacing(false);
    }
  };

  const onCancel = () => {
    askFocus();
    upload.cancel();
  };

  return (
    <section ref={sectionRef} className={classes.section} aria-labelledby="video-block-title">
      <Title ref={headingRef} id="video-block-title" order={2} size="h5" tabIndex={-1}>
        {stream ? t('stream.blockTitle') : t('block.title')}
      </Title>

      {choosing ? (
        <Stack gap="sm">
          {stream ? null : (
            <SegmentedControl
              aria-label={t('block.sourceLabel')}
              value={source}
              onChange={(value) => {
                setSource(value === 'youtube' ? 'youtube' : 'upload');
              }}
              // Held until the file is attached too: a link added meanwhile would race its attach.
              disabled={disabled || goingIn}
              data={[
                { value: 'upload', label: t('block.sourceUpload') },
                { value: 'youtube', label: t('block.sourceYoutube') },
              ]}
            />
          )}
          {source === 'upload' ? (
            <UploadPane
              upload={upload}
              disabled={disabled}
              onFile={onFile}
              onCancel={onCancel}
              onRetryAttach={onRetryAttach}
              t={t}
            />
          ) : (
            <YoutubePane
              disabled={disabled}
              busy={attachLink.isRunning}
              error={attachLink.error}
              onAdd={onLink}
              kinds={stream ? t('stream.linkNote') : t('youtube.kinds')}
              t={t}
            />
          )}
          {replacing && !goingIn ? (
            <div>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => {
                  askFocus();
                  setReplacing(false);
                  upload.dismiss();
                  attachLink.clearError();
                }}
              >
                {t('block.keepCurrent')}
              </Button>
            </div>
          ) : null}
        </Stack>
      ) : video === null ? null : (
        <CurrentVideo
          video={video}
          reading={state.reading}
          readFailure={state.readFailure}
          onReadAgain={state.readAgain}
          playbackFailed={state.playbackFailed}
          disabled={disabled}
          busy={attachLink.isRunning || remove.isRunning}
          orientationError={attachLink.error}
          onPlayerFailed={state.refresh}
          onOrientation={(orientation) => {
            if (video.youtubeVideoId === null || video.youtubeVideoId === undefined) {
              return;
            }
            const url = `https://youtu.be/${video.youtubeVideoId}`;
            void (async () => {
              const changed = await attachLink.run(url, orientation);
              if (changed !== undefined) {
                adopt(changed);
              }
            })();
          }}
          onReplace={() => {
            askFocus();
            upload.dismiss();
            attachLink.clearError();
            setSource(video.source);
            setReplacing(true);
          }}
          onRemove={onRemove}
          t={t}
        />
      )}

      {remove.error === undefined ? null : (
        <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {describeProblem(remove.error, 'video')}
        </Text>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------- choosing a file */

function UploadPane({
  upload,
  disabled,
  onFile,
  onCancel,
  onRetryAttach,
  t,
}: {
  upload: VideoUpload;
  disabled: boolean;
  onFile: (file: File) => void;
  onCancel: () => void;
  onRetryAttach: () => void;
  t: VideoT;
}) {
  const [dragging, setDragging] = useState(false);
  const resetRef = useRef<() => void>(null);
  const { state } = upload;

  if (state.phase === 'uploading') {
    const percent = Math.round(state.progress * 100);
    return (
      <Stack gap="xs" className={classes.waiting}>
        <Text size="sm" fw={600}>
          {state.fileName}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Progress
            value={percent}
            size="sm"
            style={{ flex: 1 }}
            aria-label={t('upload.progress')}
          />
          <Text size="xs" c="dimmed" aria-hidden="true">
            {percent}%
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {t('upload.sent', {
            sent: formatSize(state.byteSize * state.progress),
            total: formatSize(state.byteSize),
          })}
        </Text>
        <Text size="sm" role="status">
          {t('upload.keepOpen')}
        </Text>
        <div>
          <Button variant="default" size="compact-sm" onClick={onCancel} {...FOCUS_TARGET}>
            {t('upload.cancel')}
          </Button>
        </div>
      </Stack>
    );
  }

  if (state.phase === 'attaching') {
    return (
      <Group gap="sm" className={classes.waiting} role="status">
        <Loader size="sm" />
        <Text size="sm">{t('upload.attaching')}</Text>
      </Group>
    );
  }

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file !== undefined && !disabled) {
      onFile(file);
    }
  };

  return (
    <Stack gap="xs">
      <div
        className={classes.dropzone}
        data-dragging={dragging || undefined}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) {
            setDragging(true);
          }
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={onDrop}
      >
        <CloudUpload size={26} className={classes.dropIcon} aria-hidden />
        <Text size="sm" fw={600}>
          {t('upload.drop')}
        </Text>
        <FileButton
          accept={VIDEO_ACCEPT}
          disabled={disabled}
          inputProps={{ 'aria-label': t('upload.choose'), disabled }}
          resetRef={resetRef}
          onChange={(file) => {
            // Cleared at once, so the same file can be chosen again after a refusal.
            resetRef.current?.();
            if (file !== null && !disabled) {
              onFile(file);
            }
          }}
        >
          {(props) => (
            <Button
              {...props}
              {...FOCUS_TARGET}
              variant="default"
              size="compact-sm"
              disabled={disabled}
            >
              {t('upload.choose')}
            </Button>
          )}
        </FileButton>
        <Text className={classes.hint}>{t('upload.limits')}</Text>
      </div>
      <Text className={classes.hint}>{t('block.checkedByPerson')}</Text>
      {state.phase === 'failed' ? (
        <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {describeVideoUploadFailure(state.error, t)}
        </Text>
      ) : null}
      {state.phase === 'attachFailed' ? (
        <Stack gap={6} align="flex-start">
          <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {t('upload.notAttached')} {describeVideoUploadFailure(state.error, t)}
          </Text>
          <Button size="compact-sm" onClick={onRetryAttach} disabled={disabled}>
            {t('upload.tryAgain')}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

/* ----------------------------------------------------------------------- a YouTube link */

function YoutubePane({
  disabled,
  busy,
  error,
  onAdd,
  kinds,
  t,
}: {
  disabled: boolean;
  busy: boolean;
  error: unknown;
  onAdd: (url: string) => void;
  /** Which links work: any YouTube video's, or a live stream's own. */
  kinds: string;
  t: VideoT;
}) {
  const [link, setLink] = useState('');
  const typed = link.trim();
  return (
    <Stack gap="xs">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (typed.length > 0) {
            onAdd(typed);
          }
        }}
      >
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <TextInput
            {...FOCUS_TARGET}
            label={t('youtube.label')}
            placeholder={t('youtube.placeholder')}
            style={{ flex: 1 }}
            disabled={disabled}
            value={link}
            maxLength={500}
            error={error === undefined ? null : describeYoutubeFailure(error, t)}
            onChange={(event) => {
              setLink(event.currentTarget.value);
            }}
          />
          <Button type="submit" disabled={disabled || typed.length === 0} loading={busy}>
            {t('youtube.add')}
          </Button>
        </Group>
      </form>
      <Text className={classes.hint}>{kinds}</Text>
      <Text className={classes.hint}>{t('youtube.channelChecked')}</Text>
    </Stack>
  );
}

/* --------------------------------------------------------------------- the video as it is */

interface CurrentVideoProps {
  readonly video: ArticleVideo;
  readonly reading: boolean;
  /** Reads kept failing (./use-article-video.ts): said in place of the spinner, with a retry. */
  readonly readFailure: { readonly error: unknown } | null;
  readonly onReadAgain: () => void;
  /** The player failed past its retries (./use-article-video.ts). */
  readonly playbackFailed: boolean;
  readonly disabled: boolean;
  readonly busy: boolean;
  readonly orientationError: unknown;
  readonly onPlayerFailed: () => void;
  readonly onOrientation: (orientation: VideoOrientation) => void;
  readonly onReplace: () => void;
  readonly onRemove: () => Promise<void>;
  readonly t: VideoT;
}

function CurrentVideo({
  video,
  reading,
  readFailure,
  onReadAgain,
  playbackFailed,
  disabled,
  busy,
  orientationError,
  onPlayerFailed,
  onOrientation,
  onReplace,
  onRemove,
  t,
}: CurrentVideoProps) {
  const orientation: VideoOrientation = video.orientation ?? 'horizontal';
  // One failure per address reaches `onPlayerFailed`, which bounds how often a new one is asked for.
  const retriedFor = useRef<string | null>(null);

  const actions = disabled ? null : (
    <Group gap="xs">
      {video.state === 'failed' ? null : (
        <Button variant="default" size="compact-sm" onClick={onReplace} disabled={busy}>
          {video.source === 'youtube' ? t('block.replaceLink') : t('block.replace')}
        </Button>
      )}
      <ConfirmAction
        trigger={(open) => (
          <Button variant="default" size="compact-sm" onClick={open} disabled={busy}>
            {t('block.remove')}
          </Button>
        )}
        title={t('block.removeTitle')}
        body={video.source === 'upload' ? t('block.removeBodyFile') : t('block.removeBodyLink')}
        confirmLabel={t('block.remove')}
        destructive
        busy={busy}
        onConfirm={onRemove}
      />
    </Group>
  );

  // Where the screen waits on a read - the worker's progress, the player's address - a read that
  // keeps failing is said, with a way to read again now, in place of the spinner.
  const readFailed =
    readFailure === null ? null : (
      <ErrorState title={t('block.readFailed')} error={readFailure.error} onRetry={onReadAgain} />
    );

  if (video.state === 'uploaded' || video.state === 'processing') {
    return (
      <Stack gap="sm">
        {readFailed ?? (
          <div className={classes.waiting} role="status">
            <Loader size="sm" />
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                {t('block.processingTitle')}
              </Text>
              <Text size="sm">{t('block.processing')}</Text>
              {video.fileName === null || video.fileName === undefined ? null : (
                <Text size="xs" c="dimmed">
                  {video.fileName}
                </Text>
              )}
            </Stack>
          </div>
        )}
        {actions}
      </Stack>
    );
  }

  if (video.state === 'failed') {
    return (
      <Stack gap="sm">
        <Alert
          variant="light"
          role="alert"
          title={t('block.failedTitle')}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{describeVideoFailure(video.failureCode, t)}</Text>
        </Alert>
        {disabled ? null : (
          <Group gap="xs">
            <Button size="compact-sm" onClick={onReplace} disabled={busy} {...FOCUS_TARGET}>
              {t('block.tryAnother')}
            </Button>
            {actions}
          </Group>
        )}
      </Stack>
    );
  }

  if (video.source === 'youtube') {
    const id = video.youtubeVideoId ?? '';
    return (
      <Stack gap="sm">
        <div className={classes.frame} data-orientation={orientation}>
          <iframe
            className={classes.embed}
            src={youtubeEmbedUrl(id)}
            title={t('block.youtubePlayer')}
            allow="encrypted-media; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
        <SegmentedControl
          aria-label={t('block.orientationLabel')}
          value={orientation}
          disabled={disabled || busy}
          onChange={(value) => {
            const next: VideoOrientation = value === 'vertical' ? 'vertical' : 'horizontal';
            if (next !== orientation) {
              onOrientation(next);
            }
          }}
          data={[
            {
              value: 'horizontal',
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <RectangleHorizontal size={14} aria-hidden />
                  <span>{t('block.horizontal')}</span>
                </Group>
              ),
            },
            {
              value: 'vertical',
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <RectangleVertical size={14} aria-hidden />
                  <span>{t('block.vertical')}</span>
                </Group>
              ),
            },
          ]}
        />
        {orientationError === undefined ? null : (
          <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {describeYoutubeFailure(orientationError, t)}
          </Text>
        )}
        <Text className={classes.hint}>{t('youtube.channelChecked')}</Text>
        {actions}
      </Stack>
    );
  }

  const address = video.playbackUrl ?? null;
  return (
    <Stack gap="sm">
      {address === null && readFailed !== null ? (
        readFailed
      ) : (
        <div className={classes.frame} data-orientation={orientation}>
          {address === null ? (
            <Group justify="center" h="100%" role="status">
              {reading ? <Loader size="sm" /> : null}
              <Text size="sm" c="dimmed">
                {t('block.playerLoading')}
              </Text>
            </Group>
          ) : (
            <video
              className={classes.player}
              src={address}
              controls
              preload="metadata"
              aria-label={t('block.player')}
              onError={() => {
                if (retriedFor.current !== address) {
                  retriedFor.current = address;
                  onPlayerFailed();
                }
              }}
            />
          )}
        </div>
      )}
      {playbackFailed ? (
        <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
          {t('block.playerFailed')}
        </Text>
      ) : null}
      <VideoFacts video={video} />
      {actions}
    </Stack>
  );
}

/** "4:12 · 1080p · 240 MB" - what is known of the file, and nothing invented for what is not. */
function VideoFacts({ video }: { video: ArticleVideo }) {
  const parts = [
    video.durationMs === null || video.durationMs === undefined
      ? null
      : formatDuration(video.durationMs),
    video.widthPx === null ||
    video.widthPx === undefined ||
    video.heightPx === null ||
    video.heightPx === undefined
      ? null
      : formatResolution(video.widthPx, video.heightPx),
    video.byteSize === null || video.byteSize === undefined ? null : formatSize(video.byteSize),
  ].filter((part): part is string => part !== null);
  if (parts.length === 0) {
    return null;
  }
  return (
    <div className={classes.facts}>
      {parts.map((part) => (
        <span key={part}>{part}</span>
      ))}
    </div>
  );
}
